import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { selectConfigurationParameterById } from '!core/redux';
import { ButtonMain } from '»components/atoms/Buttons';
import { TrlTextField } from '»components/atoms/Inputs';
import { TrlFormButtons } from '»components/molecules/TrlFormButtons';
import { isAureaActiveClient } from '»utils/clientsUtils';
import { isSubmitFormButtonDisabled } from '»utils/form';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { AuthWrapper } from '../AuthWrapper';
import { Otp, otpSlice } from '../Otp';
import {
  authSlice,
  isAuthenticatedSelector,
  isAuthenticationLoading,
  loginErrorsSelector,
} from '../redux';
import { loginSlice } from './_login.slice';
import { loginCurrentStepSelector, loginIdSelector } from './login.selectors';
import { FormStyled } from './Login.styles';
import { RecoverModal } from './RecoverModal';

const AutoResetForm = (props) => {
  const { setSubmitting } = useFormikContext();

  const { isLoading, isAuthenticated } = props;

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setSubmitting(false);
    }
  }, [isLoading, isAuthenticated, setSubmitting]);

  return undefined;
};

AutoResetForm.propTypes = {
  isLoading: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
};

export const Login = () => {
  const dispatch = useDispatch();

  const [otpErrorMsg, setOtpErrorMsg] = useState(null);
  const [username, setUsername] = useState('');

  const isLoading = useSelector(isAuthenticationLoading);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const error = useSelector(loginErrorsSelector);
  const loginId = useSelector(loginIdSelector);
  const loginCurrentStep = useSelector(loginCurrentStepSelector);
  const hasOnboarding =
    useSelector((state) =>
      selectConfigurationParameterById(state, 'ONBOARDING'),
    )?.value === 'Y';

  const NEW_ACCOUNT_EXP = isAureaActiveClient
    ? 'TRLT_UI-OPEN_ACCOUNT'
    : 'TRLT_UI-CREATE_ACCOUNT';

  const LOGIN_EXP = isAureaActiveClient
    ? 'TRLT_UI-ACCESS_ACCOUNT'
    : 'TRLT_UI-LOGIN';

  const browserUrl = window.location.pathname.toLowerCase();
  const shouldOpenLoginFromUrl = browserUrl.includes('/login');

  useEffect(() => {
    if (shouldOpenLoginFromUrl) window.history.replaceState({}, '', '/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (values) => {
    dispatch(loginSlice.actions.loginRequest(values));
  };

  const validateForm = (values) => {
    const errors = {};

    if (!values.password)
      errors.password = <FormattedMessage id="TRLT_UI-REQUIRED_FIELD" />;

    if (!values.user || (values.user && values.user.trim().length === 0)) {
      errors.user = <FormattedMessage id="TRLT_UI-REQUIRED_FIELD" />;
    } else if (values.user && /\s/.test(values.user.trim())) {
      errors.user = <FormattedMessage id="TRLT_UI-NO_SPACES_ALLOW" />;
    }

    return errors;
  };

  const handleSubmitOtpForm = ({ validationCode }) => {
    dispatch(
      otpSlice.actions.validateLoginOtpRequest({
        username,
        loginId,
        otp: validationCode,
      }),
    );
  };

  const handleGetOptError = (event) => {
    setOtpErrorMsg(event);
  };

  const validateOtpForm = (values) => {
    const errors = {};

    if (!values.validationCode)
      errors.validationCode = <FormattedMessage id="TRLT_UI-REQUIRED_FIELD" />;

    return errors;
  };

  if (loginCurrentStep === 0)
    return (
      <AuthWrapper
        screenTitle={LOGIN_EXP}
        screenDescr="TRLT_UI-INFO_TRADING"
        screenSecondDescr={hasOnboarding ? 'TRLT_UI-NOT_CLIENT' : ''}
        linkText={NEW_ACCOUNT_EXP}
        linkRoute="/Onboarding"
        linkMenuItem="ONBOARDING"
        showFeedbackMsg={!!error}
        feedbackMsg="TRLT_UI-INVALID_LOGIN"
        handleCloseFeedback={() => {
          dispatch(authSlice.actions.resetAuth());
        }}
      >
        <Formik
          initialValues={{ user: '', password: '' }}
          onSubmit={handleSubmit}
          validate={validateForm}
          enableReinitialize
        >
          {({ isSubmitting, errors, dirty, values }) => (
            <FormStyled className="formContainer">
              <TrlTextField
                name="user"
                type="text"
                label={<FormattedMessage id="TRLT_UI-USER" />}
                id="user"
                autoComplete="username"
                disabled={isLoading}
                placeholder={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-USER_PLACEHOLDER',
                })}
                additionalFunc={(event) => {
                  setUsername(event.target.value);
                }}
              />

              <TrlTextField
                name="password"
                type="password"
                label={<FormattedMessage id="TRLT_UI-PASSWORD" />}
                id="password"
                autoComplete="current-password"
                disabled={isLoading}
                placeholder={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-PASSWORD_PLACEHOLDER',
                })}
              />
              <RecoverModal isDisabled={isLoading} />

              <ButtonMain
                className="submitButton"
                expressionId={LOGIN_EXP}
                type="submit"
                disabled={isSubmitFormButtonDisabled({
                  isSubmitting,
                  dirty,
                  errors,
                  values,
                  areFiles: false,
                })}
                id="loginSubmitBtn"
              />
              <AutoResetForm
                isLoading={isLoading}
                isAuthenticated={isAuthenticated}
              />
            </FormStyled>
          )}
        </Formik>
      </AuthWrapper>
    );

  return (
    <AuthWrapper
      screenTitle="TRLT_UI-OTP_VALIDATION"
      screenDescr="TRLT_UI-OTP_VALIDATION_DETAIL"
      showFeedbackMsg={!!error || !!otpErrorMsg}
      feedbackMsg={otpErrorMsg || 'TRLT_UI-INVALID_OTP'}
    >
      <Formik
        initialValues={{ validationCode: '' }}
        onSubmit={handleSubmitOtpForm}
        validate={validateOtpForm}
        enableReinitialize
      >
        {({ isSubmitting, dirty, errors, values }) => (
          <FormStyled className="formContainer">
            <Otp
              textFieldName="validationCode"
              onError={handleGetOptError}
              username={username}
            />

            <TrlFormButtons
              advanceButtonName="TRLT_UI-VALIDATE"
              isAdvanceButtonDisabled={isSubmitFormButtonDisabled({
                isSubmitting,
                dirty,
                errors,
                values,
                areFiles: false,
              })}
            />
            <AutoResetForm
              isLoading={isLoading}
              isAuthenticated={isAuthenticated}
            />
          </FormStyled>
        )}
      </Formik>
    </AuthWrapper>
  );
};
